import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TextField, Autocomplete, Tooltip, InputAdornment, IconButton } from '@mui/material';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import InfoIcon from '@mui/icons-material/Info';
import { AuthContext } from '../../context/AuthContext';
import CourseEnrollmentModal from './CourseEnrollmentModal';
import { getAssignableRoles } from '../../utils/roleHierarchy';

// Adapter para integrar o react-phone-number-input com o TextField do MUI
const PhoneInputAdapter = React.forwardRef(function PhoneInputAdapter(props, ref) {
  return <TextField {...props} inputRef={ref} fullWidth variant="outlined" />;
});

export default function UserEditModal({ user, onClose, onSuccess, isSubmitting, companies: companiesProp }) {
  const { user: currentUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({});
  const [companies, setCompanies] = useState(companiesProp || []);
  const [loading, setLoading] = useState(!Array.isArray(companiesProp) || companiesProp.length === 0);
  const [errors, setErrors] = useState({});
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [createdUserName, setCreatedUserName] = useState(null);

  // Obter roles disponíveis com base na hierarquia
  const assignableRoles = getAssignableRoles(currentUser?.role);
  const roleOptions = [
    { id: 'STUDENT', name: 'Aluno' },
    { id: 'INSTRUCTOR', name: 'Instrutor' },
    { id: 'ADMIN_CLIENT', name: 'Admin (Cliente)' },
    { id: 'ADMIN_EAD', name: 'Admin (EAD)' },
    { id: 'SUPER_ADMIN', name: 'Super Admin' }
  ].filter(role => assignableRoles.includes(role.id));
  const isCreating = !user?.id;

  useEffect(() => {
    // Define o estado inicial do formulário com base no usuário passado
    const initialState = {
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      role: 'STUDENT',
      companyId: null,
      password: '',
      phoneNumber: ''
    };

    let isMounted = true;

    const fetchCompanies = async () => {
      // Se o componente pai já passou as companies via props, não buscar novamente
      if (Array.isArray(companiesProp) && companiesProp.length > 0) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await api.get('/companies');
        if (isMounted) {
          setCompanies(res.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Erro ao carregar empresas", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanies();

    if (user) {
      // Se estiver editando, preenche com os dados do usuário
      // Garante que todos os campos do estado inicial existam para evitar "uncontrolled to controlled"
      const userData = { 
        ...initialState, 
        ...user, 
        companyId: user.company?.id || null, 
        password: '',
        phoneNumber: user.phoneNumber || '' // Garante que o telefone seja carregado
      };
      // O campo fullName é dividido em firstName e lastName se estes não vierem da API
      if (user.fullName && !user.firstName) {
        const nameParts = user.fullName.split(' ');
        userData.firstName = nameParts.shift();
        userData.lastName = nameParts.join(' ');
      }
      setFormData(userData);
    } else {
      // Se estiver criando, usa o estado inicial puro
      setFormData(initialState);
    }

    // Função de limpeza para evitar atualizações de estado em um componente desmontado
    return () => {
      isMounted = false;
    };
  }, [user, companiesProp]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const newErrors = { ...errors };

    // Valida o nome de usuário em tempo real para feedback imediato
    if (name === 'userName' && /[A-Z]|\s/.test(value)) {
        newErrors.userName = 'Use apenas letras minúsculas e sem espaços.';
    } else {
      // Limpa o erro do campo atual ao ser modificado.
      // Isso resolve o problema de erros antigos (ex: email em uso) bloqueando o formulário.
      delete newErrors[name];
    }
    setErrors(newErrors);
    setFormData(prev => ({
      ...prev,
      [name]: name === 'companyId' && value === '' ? null : value
    }));
  };

  const handleAutocompleteChange = (name, newValue) => {
    setFormData(prev => ({
      ...prev,
      [name]: newValue ? newValue.id : null
    }));
  };

  const handlePhoneChange = (value) => {
    const newErrors = { ...errors };
    // Valida se o número de telefone é possível (não verifica se é 100% válido)
    if (value && !isPossiblePhoneNumber(value)) {
      newErrors.phoneNumber = 'Número de telefone inválido.';
    } else {
      delete newErrors.phoneNumber;
    }
    setErrors(newErrors);

    setFormData(prev => ({
      ...prev,
      phoneNumber: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'O nome é obrigatório.';
    if (!formData.userName) newErrors.userName = 'O nome de usuário é obrigatório.';
    if (!formData.email) newErrors.email = 'O email é obrigatório.';
    if (!formData.role) newErrors.role = 'O perfil é obrigatório.';
    if (isCreating && !formData.password) newErrors.password = 'A senha é obrigatória na criação.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação no frontend antes de enviar
    if (!validateForm()) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Combina com erros de validação em tempo real (ex: username com espaço)
    const currentErrors = { ...errors };
    delete currentErrors.password; // Não bloqueia por erro de senha se não for obrigatório

    if (Object.keys(currentErrors).length > 0) {
      toast.error('Por favor, corrija os erros no formulário.');
      return;
    }

    const dataToSave = { ...formData };

    // Filtra a senha se estiver vazia na edição para não sobrescrever com um valor vazio
    if (!isCreating && !dataToSave.password) {
      delete dataToSave.password;
    }

    try {
      const result = await onSuccess(dataToSave, isCreating);
      
      // Se está criando um usuário e o resultado contém o ID, abre o modal de cursos
      if (isCreating && result?.id) {
        // Verifica se o role permite matricular em cursos
        // SUPER_ADMIN não precisa ser matriculado em cursos
        const rolesWithEnrollment = ['ADMIN_EAD', 'INSTRUCTOR', 'ADMIN_CLIENT', 'STUDENT'];
        if (rolesWithEnrollment.includes(formData.role)) {
          setCreatedUserId(result.id);
          setCreatedUserName(result.fullName || formData.firstName);
          setEnrollmentModalOpen(true);
        } else {
          // Para SUPER_ADMIN, apenas fecha os modais
          onClose();
        }
      }
    } catch (error) {
      // Erros específicos de campo devem ser tratados na função onSuccess
      console.error("Erro ao salvar usuário:", error);
      toast.error(error.response?.data?.message || 'Erro ao salvar usuário. Tente novamente.');
    }
  };

  if (!user && !isCreating) return null;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal card" onClick={e => e.stopPropagation()}>
        <div className="card-body">
          <div className="modal-header">
            <h3>{isCreating ? 'Criar Novo Usuário' : 'Editar Usuário'}</h3>
            <button onClick={onClose} className="btn btn-secondary">✕</button>
          </div>

          {loading ? (
            <div className="loading-state">Carregando...</div>
          ) : (
            <form onSubmit={handleSubmit} id="user-edit-form" className="modal-form">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <TextField
                  label="Nome"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                  fullWidth
                />
                <TextField
                  label="Sobrenome"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
                <TextField
                  label="Nome de Usuário"
                  name="userName"
                  value={formData.userName || ''}
                  onChange={handleInputChange}
                  error={!!errors.userName}
                  helperText={errors.userName}
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Será usado para login e URL de perfil. Use apenas letras minúsculas e sem espaços.">
                          <IconButton tabIndex={-1} type="button"><InfoIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <div style={{ gridColumn: 'span 2' }}>
                  <PhoneInput
                    placeholder="Telefone (Opcional)"
                    value={formData.phoneNumber || ''}
                    onChange={handlePhoneChange}
                    defaultCountry="BR"
                    inputComponent={PhoneInputAdapter}
                    label="Telefone"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                  />
                </div>
                <Autocomplete
                  options={roleOptions}
                  getOptionLabel={(option) => option.name || ''}
                  value={roleOptions.find(r => r.id === formData.role) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      role: newValue ? newValue.id : 'STUDENT'
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Perfil (Role)" required error={!!errors.role} helperText={errors.role} InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment position="end">
                            <Tooltip title="Define as permissões do usuário no sistema.">
                              <IconButton tabIndex={-1} type="button"><InfoIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </InputAdornment>
                        </>
                      ),
                    }} />
                  )}
                  noOptionsText="Nenhum perfil encontrado"
                  fullWidth
                />
                <Autocomplete
                  options={companies}
                  getOptionLabel={(option) => option.name || ''}
                  value={companies.find(c => c.id === formData?.companyId) || null}
                  onChange={(event, newValue) => {
                    handleAutocompleteChange('companyId', newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Empresa" InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment position="end">
                            <Tooltip title="Associa o usuário a uma empresa. Deixe em branco se não aplicável.">
                              <IconButton tabIndex={-1} type="button"><InfoIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </InputAdornment>
                        </>
                      ),
                    }}
                    />
                  )}
                  noOptionsText="Nenhuma empresa encontrada"
                  fullWidth
                />
                <TextField
                  label="Senha"
                  type="password"
                  name="password"
                  value={formData.password || ''}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  placeholder={isCreating ? 'Obrigatório' : 'Deixe em branco para não alterar'}
                  required={isCreating}
                  style={{ gridColumn: 'span 2' }}
                  fullWidth
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
                <button type="submit" form="user-edit-form" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal de seleção de cursos (aberto após criar o usuário) */}
      <CourseEnrollmentModal
        open={enrollmentModalOpen}
        onClose={() => {
          setEnrollmentModalOpen(false);
          setCreatedUserId(null);
          setCreatedUserName(null);
          onClose(); // Fecha o modal de criação de usuário também
        }}
        userId={createdUserId}
        userName={createdUserName}
        onEnrollmentSuccess={() => {
          // Após matricular, fecha ambos os modais
          setEnrollmentModalOpen(false);
          setCreatedUserId(null);
          setCreatedUserName(null);
          onClose();
        }}
      />
    </div>
  );
}